import { HttpStatusCode } from "axios";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilState, useRecoilValue, useSetRecoilState } from "recoil";

import {
  apiDataFetchingAtom,
  apiDataFetchingErrorsAtom,
  ApiException,
  apiPatch,
  apiPost,
  applyBulkAllSkippedFeedback,
  castErrorResult,
  companyIdSelector,
  ConfirmResult,
  createErrorResult,
  createErrorResultFromException,
  createErrorResultFromTranslatableItem,
  createSuccessResult,
  DrawerType,
  errors,
  getRideBulkCloneRidesPath,
  getRideBulkFetchTrackingHistoryPath,
  getRideBulkFitVehicleAndRecalculatePath,
  getRideBulkRecalculatePricePath,
  getRideBulkRecalculateSupplierPricePath,
  getRideBulkRejectPath,
  getRideBulkSendSMSPath,
  getRideBulkSendToSupplierPath,
  getRideBulkStatusDoneApprovedPath,
  getRideBulkStatusDonePath,
  getRideBulkStatusNewPath,
  getRideBulkStatusReceivedPath,
  getRidesJourneyAssignBulkPath,
  IApiError,
  IBulkRideRebuildPayload,
  IBulkUpdateStationsPayload,
  IRideArchiveCancelApiPayload,
  IRideBulkRebuildRoutesPayload,
  IRydeProcess,
  isHardSoftValidationBulkErrorResponse,
  isSoftValidationBulkErrorResponse,
  isTranslatableItem,
  Log,
  mapHardValidationErrorToResult,
  normalizeErrors,
  normalizeRideEntity,
  RideDeleteActionsResultEnum,
  RidesApi,
  RideStatusEnum,
  RydeProcessTitle,
  SimpleResult,
  simpleResultErrorEmptyResponse,
  simpleResultErrorInfo,
  simpleResultInvalidApiResponse,
  starredTableRowsIdsAtomFamily,
  TError,
  toForbiddenTranslatableItem,
  translateInfoItem,
  useDrawers,
  warnAndToastSimpleResult,
} from "@ryde/shared";

import { usePageContext } from "../../contexts";
import { useSoftValidationModalConfirm } from "../use-soft-validation-modal-confirms";

export interface IBulkCloneRidesPayload {
  ids: number[];
  dateFrom: TDateISO;
  dateTo: TDateISO;
  dayGroup: number[];
  dayType: number[];
  numberOfDuplicates: number;
  journeyId: number | null;
}

export const useRidesBulkActions = () => {
  const { entity: entityFromContext } = usePageContext();
  const entity = normalizeRideEntity(entityFromContext);
  const setRidesErrors = useSetRecoilState(apiDataFetchingErrorsAtom);
  const setRidesFetching = useSetRecoilState(apiDataFetchingAtom);
  const companyId = useRecoilValue(companyIdSelector);
  const { t } = useTranslation();
  const { confirmSoftValidationAction } = useSoftValidationModalConfirm();
  const { openDrawer } = useDrawers();

  const consumeBulkAllSkipped = (result: SimpleResult<unknown>): boolean =>
    applyBulkAllSkippedFeedback({
      result,
      translationFunction: t,
      setErrors: setRidesErrors,
    });

  const [starredIds, setStarredIds] = useRecoilState(
    starredTableRowsIdsAtomFamily(entity),
  );

  const bulkStarRides = useEvent((dataIds: number[]) => {
    const starredSet = new Set(starredIds);
    const allStarred = dataIds.every(id => starredSet.has(id));

    if (allStarred) {
      const dataIdsSet = new Set(dataIds);
      setStarredIds(x => x.filter(id => !dataIdsSet.has(id)));
    } else {
      const updatedIds = new Set([...starredIds, ...dataIds]);
      setStarredIds([...updatedIds]);
    }
  });

  const bulkReject = useEvent(async (ids: number[]): Promise<boolean> => {
    try {
      setRidesErrors(undefined);
      setRidesFetching(true);
      const requestUrl = getRideBulkRejectPath(companyId || 0);
      const rejectRidesResult = await apiPatch<SimpleResult<IRydeProcess>>(
        requestUrl,
        {
          body: { ids },
        },
      );

      if (!rejectRidesResult) {
        setRidesErrors([t(errors.common.emptyApiResponse)]);
        return false;
      }

      if (!rejectRidesResult.success) {
        if (consumeBulkAllSkipped(rejectRidesResult)) {
          return false;
        }
        const error = rejectRidesResult.error;

        if (isTranslatableItem(error)) {
          setRidesErrors([translateInfoItem(error, t)]);
        } else {
          setRidesErrors([t("errors.failedToRejectRides")]);
        }

        return false;
      }

      const resultData = rejectRidesResult.data as IRydeProcess;

      openDrawer<DrawerType.ProcessDrawer>({
        drawerProps: {
          type: DrawerType.ProcessDrawer,
          processId: resultData.id,
          processTitle: RydeProcessTitle.BulkRideReject,
          currentEntityPage: entityFromContext,
          entity,
        },
      });
      return true;
    } catch (error) {
      if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
        const errorDetails = toForbiddenTranslatableItem(
          error,
          errors.common.dontHavePermissionForBulkAction,
        );
        Log.errorAndToast(t("errors.failedToRejectRides"), errorDetails);
        setRidesErrors([translateInfoItem(errorDetails, t)]);
        return false;
      }

      Log.errorAndToast(t("errors.failedToRejectRides"), error);
      setRidesErrors(normalizeErrors(error as IApiError, t));
      return false;
    } finally {
      setRidesFetching(false);
    }
  });

  const bulkStatusNew = useEvent(async (ids: number[]): Promise<boolean> => {
    try {
      setRidesFetching(true);
      const requestUrl = getRideBulkStatusNewPath(companyId || 0);
      const statusNewRidesResult = await apiPatch<SimpleResult<IRydeProcess>>(
        requestUrl,
        {
          body: { ids },
        },
      );

      if (!statusNewRidesResult) {
        setRidesErrors([t(errors.common.emptyApiResponse)]);
        return false;
      }

      if (!statusNewRidesResult.success) {
        if (consumeBulkAllSkipped(statusNewRidesResult)) {
          return false;
        }
        const error = statusNewRidesResult.error;

        if (isTranslatableItem(error)) {
          setRidesErrors([translateInfoItem(error, t)]);
        } else {
          setRidesErrors([
            t("errors.failedToSetRidesStatus", { status: RideStatusEnum.New }),
          ]);
        }

        return false;
      }

      const resultData = statusNewRidesResult.data as IRydeProcess;

      openDrawer<DrawerType.ProcessDrawer>({
        drawerProps: {
          type: DrawerType.ProcessDrawer,
          processId: resultData.id,
          processTitle: RydeProcessTitle.BulkRideStatusNew,
          currentEntityPage: entityFromContext,
          entity,
        },
      });
      return true;
    } catch (error) {
      if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
        const errorDetails = toForbiddenTranslatableItem(
          error,
          errors.common.dontHavePermissionForBulkAction,
        );
        Log.errorAndToast(
          t("errors.failedToSetRidesStatus", { status: RideStatusEnum.New }),
          errorDetails,
        );
        setRidesErrors([translateInfoItem(errorDetails, t)]);
        return false;
      }

      Log.errorAndToast(
        t("errors.failedToSetRidesStatus", { status: RideStatusEnum.New }),
        error,
      );
      setRidesErrors(normalizeErrors(error as IApiError, t));
      return false;
    } finally {
      setRidesFetching(false);
    }
  });

  const bulkStatusDone = useEvent(async (ids: number[]): Promise<boolean> => {
    try {
      setRidesFetching(true);
      const requestUrl = getRideBulkStatusDonePath(companyId || 0);
      const statusDoneRidesResult = await apiPatch<SimpleResult<IRydeProcess>>(
        requestUrl,
        {
          body: { ids },
        },
      );

      if (!statusDoneRidesResult) {
        setRidesErrors([t(errors.common.emptyApiResponse)]);
        return false;
      }

      if (!statusDoneRidesResult.success) {
        if (consumeBulkAllSkipped(statusDoneRidesResult)) {
          return false;
        }
        const error = statusDoneRidesResult.error;

        if (isTranslatableItem(error)) {
          setRidesErrors([translateInfoItem(error, t)]);
        } else {
          setRidesErrors([
            t("errors.failedToSetRidesStatus", { status: RideStatusEnum.Done }),
          ]);
        }

        return false;
      }

      const resultData = statusDoneRidesResult.data as IRydeProcess;

      openDrawer<DrawerType.ProcessDrawer>({
        drawerProps: {
          type: DrawerType.ProcessDrawer,
          processId: resultData.id,
          processTitle: RydeProcessTitle.BulkRideStatusDone,
          currentEntityPage: entityFromContext,
          entity,
        },
      });
      return true;
    } catch (error) {
      if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
        const errorDetails = toForbiddenTranslatableItem(
          error,
          errors.common.dontHavePermissionForBulkAction,
        );
        Log.errorAndToast(
          t("errors.failedToSetRidesStatus", { status: RideStatusEnum.Done }),
          errorDetails,
        );
        setRidesErrors([translateInfoItem(errorDetails, t)]);
        return false;
      }

      Log.errorAndToast(
        t("errors.failedToSetRidesStatus", { status: RideStatusEnum.Done }),
        error,
      );
      setRidesErrors(normalizeErrors(error as IApiError, t));
      return false;
    } finally {
      setRidesFetching(false);
    }
  });

  const bulkStatusDoneApproved = useEvent(
    async (ids: number[]): Promise<boolean> => {
      try {
        setRidesFetching(true);
        const requestUrl = getRideBulkStatusDoneApprovedPath(companyId || 0);
        const statusDoneApprovedRidesResult = await apiPatch<
          SimpleResult<IRydeProcess>
        >(requestUrl, {
          body: { ids },
        });

        if (!statusDoneApprovedRidesResult) {
          setRidesErrors([t(errors.common.emptyApiResponse)]);
          return false;
        }

        if (!statusDoneApprovedRidesResult.success) {
          if (consumeBulkAllSkipped(statusDoneApprovedRidesResult)) {
            return false;
          }
          const error = statusDoneApprovedRidesResult.error;

          if (isTranslatableItem(error)) {
            setRidesErrors([translateInfoItem(error, t)]);
          } else {
            setRidesErrors([
              t("errors.failedToSetRidesStatus", {
                status: RideStatusEnum.DoneApproved,
              }),
            ]);
          }

          return false;
        }

        const resultData = statusDoneApprovedRidesResult.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkRideStatusDoneApproved,
            currentEntityPage: entityFromContext,
            entity,
          },
        });
        return true;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.failedToSetRidesStatus", {
              status: RideStatusEnum.DoneApproved,
            }),
            errorDetails,
          );
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return false;
        }

        Log.errorAndToast(
          t("errors.failedToSetRidesStatus", {
            status: RideStatusEnum.DoneApproved,
          }),
          error,
        );
        setRidesErrors(normalizeErrors(error as IApiError, t));
        return false;
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkStatusReceived = useEvent(
    async (ids: number[]): Promise<boolean> => {
      try {
        setRidesFetching(true);
        const requestUrl = getRideBulkStatusReceivedPath(companyId || 0);
        const statusReceivedRidesResult = await apiPatch<
          SimpleResult<IRydeProcess>
        >(requestUrl, {
          body: { ids },
        });

        if (!statusReceivedRidesResult) {
          setRidesErrors([t(errors.common.emptyApiResponse)]);
          return false;
        }

        if (!statusReceivedRidesResult.success) {
          if (consumeBulkAllSkipped(statusReceivedRidesResult)) {
            return false;
          }
          const error = statusReceivedRidesResult.error;

          if (isTranslatableItem(error)) {
            setRidesErrors([translateInfoItem(error, t)]);
          } else {
            setRidesErrors([
              t("errors.failedToSetRidesStatus", {
                status: RideStatusEnum.Received,
              }),
            ]);
          }

          return false;
        }

        const resultData = statusReceivedRidesResult.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkRideStatusReceived,
            currentEntityPage: entityFromContext,
            entity,
          },
        });
        return true;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.failedToSetRidesStatus", {
              status: RideStatusEnum.Received,
            }),
            errorDetails,
          );
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return false;
        }

        Log.errorAndToast(
          t("errors.failedToSetRidesStatus", {
            status: RideStatusEnum.Received,
          }),
          error,
        );
        setRidesErrors(normalizeErrors(error as IApiError, t));
        return false;
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkArchive = useEvent(
    async (
      ids: number[],
      bookingAction: RideDeleteActionsResultEnum,
    ): Promise<boolean> => {
      try {
        setRidesErrors(undefined);
        setRidesFetching(true);
        const apiPayload: IRideArchiveCancelApiPayload = {
          ids,
          bookingAction,
        };
        let bulkResult = await RidesApi.bulkArchiveRides(apiPayload);

        if (isSoftValidationBulkErrorResponse(bulkResult?.data)) {
          setRidesFetching(false);

          const softValidationResult = await confirmSoftValidationAction(
            bulkResult.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return false;
          }

          setRidesFetching(true);
          apiPayload.token = bulkResult.data.token;
          bulkResult = await RidesApi.bulkArchiveRides(apiPayload);
        } else if (isHardSoftValidationBulkErrorResponse(bulkResult?.data)) {
          const hardValidationError =
            bulkResult.data.items[0].validationErrors[0];
          setRidesFetching(false);
          const errorResult =
            mapHardValidationErrorToResult(hardValidationError);
          warnAndToastSimpleResult(errorResult, t);
          return false;
        }

        if (!bulkResult?.success) {
          if (bulkResult && consumeBulkAllSkipped(bulkResult)) {
            return false;
          }
          setRidesErrors([t(errors.common.emptyApiResponse)]);
          return false;
        }

        const resultData = bulkResult?.data as IRydeProcess;
        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkArchive,
            entity,
          },
        });
        return true;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(t("errors.failedToArchiveRides"), errorDetails);
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return false;
        }

        Log.errorAndToast(t("errors.failedToArchiveRides"), error);
        setRidesErrors(normalizeErrors(error as IApiError, t));
        return false;
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkCancel = useEvent(
    async (
      ids: number[],
      bookingAction: RideDeleteActionsResultEnum,
      // RYDE-7633: collected once for the selection when it holds a past taxi-supplier ride.
      cancelationReason?: string,
    ): Promise<SimpleResult<unknown>> => {
      try {
        setRidesErrors(undefined);
        setRidesFetching(true);
        const apiPayload: IRideArchiveCancelApiPayload = {
          ids,
          bookingAction,
          cancelationReason,
        };

        let bulkCancelResult = await RidesApi.bulkCancelRides(apiPayload);

        if (isSoftValidationBulkErrorResponse(bulkCancelResult?.data)) {
          setRidesFetching(false);

          const softValidationResult = await confirmSoftValidationAction(
            bulkCancelResult.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return createSuccessResult();
          }

          setRidesFetching(true);
          apiPayload.token = bulkCancelResult.data.token;
          bulkCancelResult = await RidesApi.bulkCancelRides(apiPayload);
        } else if (
          isHardSoftValidationBulkErrorResponse(bulkCancelResult?.data)
        ) {
          const hardValidationError =
            bulkCancelResult.data.items[0].validationErrors[0];
          setRidesFetching(false);
          const errorResult =
            mapHardValidationErrorToResult(hardValidationError);
          warnAndToastSimpleResult(errorResult, t);
          return errorResult;
        }

        if (!bulkCancelResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!bulkCancelResult.success) {
          if (consumeBulkAllSkipped(bulkCancelResult)) {
            return castErrorResult(bulkCancelResult);
          }
          setRidesErrors([t(errors.common.emptyApiResponse)]);
          return castErrorResult(bulkCancelResult);
        }

        const resultData = bulkCancelResult?.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkRideCancel,
            currentEntityPage: entityFromContext,
            entity,
          },
        });
        return bulkCancelResult;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(t("errors.failedToCancelRides"), errorDetails);
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        Log.errorAndToast(t("errors.failedToCancelRides"), normalizedError);
        setRidesErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkRebuildRoutes = useEvent(
    async (
      ids: number[],
      options?: Omit<IRideBulkRebuildRoutesPayload, "ids" | "token">,
    ): Promise<SimpleResult<unknown>> => {
      try {
        setRidesErrors(undefined);
        setRidesFetching(true);

        const apiPayload: IRideBulkRebuildRoutesPayload = {
          ids,
          ...options,
        };

        let bulkResult = await RidesApi.bulkRebuildRoutesRides(apiPayload);

        if (isSoftValidationBulkErrorResponse(bulkResult?.data)) {
          setRidesFetching(false);

          const softValidationResult = await confirmSoftValidationAction(
            bulkResult.data,
          );

          if (softValidationResult === ConfirmResult.CANCEL) {
            return createSuccessResult();
          }

          setRidesFetching(true);
          apiPayload.token = bulkResult.data.token;
          bulkResult = await RidesApi.bulkRebuildRoutesRides(apiPayload);
        } else if (isHardSoftValidationBulkErrorResponse(bulkResult?.data)) {
          const hardValidationError =
            bulkResult.data.items[0].validationErrors[0];
          setRidesFetching(false);
          const errorResult =
            mapHardValidationErrorToResult(hardValidationError);
          warnAndToastSimpleResult(errorResult, t);
          return errorResult;
        }

        if (!bulkResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!bulkResult.success) {
          if (consumeBulkAllSkipped(bulkResult)) {
            return castErrorResult(bulkResult);
          }
          setRidesErrors([t(errors.common.emptyApiResponse)]);
          return castErrorResult(bulkResult);
        }

        const resultData = bulkResult.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkRideRebuildRoute,
            currentEntityPage: entityFromContext,
            entity,
          },
        });
        return bulkResult;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(t("errors.failedToRebuildRides"), errorDetails);
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        Log.errorAndToast(t("errors.failedToRebuildRides"), normalizedError);
        setRidesErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkRecalculatePrice = useEvent(
    async ({
      ids,
      shouldIncludeManualPrices,
      shouldClearManualPriceAndSetDraftIfNoRate,
      recalculateDoneRides,
      allowSupplierChange,
      allowVehicleTypeChange,
      allowPriceRateChange,
    }: {
      ids: number[];
      shouldIncludeManualPrices?: boolean;
      shouldClearManualPriceAndSetDraftIfNoRate?: boolean;
      recalculateDoneRides?: boolean;
      allowSupplierChange?: boolean;
      allowVehicleTypeChange?: boolean;
      allowPriceRateChange?: boolean;
    }): Promise<SimpleResult<IRydeProcess>> => {
      try {
        setRidesErrors(undefined);
        setRidesFetching(true);
        const requestUrl = getRideBulkRecalculatePricePath(companyId || 0);
        const recalculatePriceResult = await apiPatch<
          SimpleResult<IRydeProcess>
        >(requestUrl, {
          body: {
            ids,
            ...(shouldIncludeManualPrices ? { includeManualPrices: true } : {}),
            ...(shouldClearManualPriceAndSetDraftIfNoRate
              ? { shouldClearManualPriceAndSetDraftIfNoRate: true }
              : {}),
            ...(recalculateDoneRides ? { recalculateDoneRides: true } : {}),
            ...(allowSupplierChange ? { allowSupplierChange: true } : {}),
            ...(allowVehicleTypeChange ? { allowVehicleTypeChange: true } : {}),
            ...(allowPriceRateChange ? { allowPriceRateChange: true } : {}),
          },
        });

        if (!recalculatePriceResult) {
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            "Empty api response",
          );
          return simpleResultErrorEmptyResponse(t);
        }

        if (recalculatePriceResult.success && !recalculatePriceResult.data) {
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            "Invalid api response",
          );
          return simpleResultInvalidApiResponse(t);
        }

        if (!recalculatePriceResult.success) {
          if (consumeBulkAllSkipped(recalculatePriceResult)) {
            return recalculatePriceResult;
          }
          warnAndToastSimpleResult(recalculatePriceResult, t);
          return recalculatePriceResult;
        }

        if ((recalculatePriceResult.data as IRydeProcess)?.id) {
          openDrawer<DrawerType.ProcessDrawer>({
            drawerProps: {
              type: DrawerType.ProcessDrawer,
              processId: (recalculatePriceResult.data as IRydeProcess).id,
              processTitle: RydeProcessTitle.BulkRecalculatePrice,
              currentEntityPage: entityFromContext,
              entity,
            },
          });
        }

        return recalculatePriceResult;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            errorDetails,
          );
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return createErrorResultFromTranslatableItem({
            translatableItem: errorDetails,
          });
        }

        Log.errorAndToast(t("errors.failedToRecalculateRidesPrice"), error);
        setRidesErrors(normalizeErrors(error as IApiError, t));
        return createErrorResultFromException(error as TError);
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkRecalculateSupplierPrice = useEvent(
    async ({
      ids,
      shouldIncludeManualPrices,
      recalculateDoneRides,
    }: {
      ids: number[];
      shouldIncludeManualPrices?: boolean;
      recalculateDoneRides?: boolean;
    }): Promise<SimpleResult<IRydeProcess>> => {
      try {
        setRidesErrors(undefined);
        setRidesFetching(true);
        const requestUrl = getRideBulkRecalculateSupplierPricePath(
          companyId || 0,
        );
        const recalculatePriceResult = await apiPatch<
          SimpleResult<IRydeProcess>
        >(requestUrl, {
          body: {
            ids,
            ...(shouldIncludeManualPrices ? { includeManualPrices: true } : {}),
            ...(recalculateDoneRides ? { recalculateDoneRides: true } : {}),
          },
        });

        if (!recalculatePriceResult) {
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            "Empty api response",
          );
          return simpleResultErrorEmptyResponse(t);
        }

        if (recalculatePriceResult.success && !recalculatePriceResult.data) {
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            "Invalid api response",
          );
          return simpleResultInvalidApiResponse(t);
        }

        if (!recalculatePriceResult.success) {
          if (consumeBulkAllSkipped(recalculatePriceResult)) {
            return recalculatePriceResult;
          }
          warnAndToastSimpleResult(recalculatePriceResult, t);
          return recalculatePriceResult;
        }

        if ((recalculatePriceResult.data as IRydeProcess)?.id) {
          openDrawer<DrawerType.ProcessDrawer>({
            drawerProps: {
              type: DrawerType.ProcessDrawer,
              processId: (recalculatePriceResult.data as IRydeProcess).id,
              processTitle: RydeProcessTitle.BulkRecalculateSupplierPrice,
              currentEntityPage: entityFromContext,
              entity,
            },
          });
        }

        return recalculatePriceResult;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            errorDetails,
          );
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return createErrorResultFromTranslatableItem({
            translatableItem: errorDetails,
          });
        }

        Log.errorAndToast(t("errors.failedToRecalculateRidesPrice"), error);
        setRidesErrors(normalizeErrors(error as IApiError, t));
        return createErrorResultFromException(error as TError);
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkFitVehicleAndRecalculate = useEvent(
    async (ids: number[]): Promise<SimpleResult<IRydeProcess>> => {
      try {
        setRidesErrors(undefined);
        setRidesFetching(true);
        const requestUrl = getRideBulkFitVehicleAndRecalculatePath(
          companyId || 0,
        );
        const result = await apiPatch<SimpleResult<IRydeProcess>>(requestUrl, {
          body: { ids },
        });

        if (!result) {
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            "Empty api response",
          );
          return simpleResultErrorEmptyResponse(t);
        }

        if (result.success && !result.data) {
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            "Invalid api response",
          );
          return simpleResultInvalidApiResponse(t);
        }

        if (!result.success) {
          if (consumeBulkAllSkipped(result)) {
            return result;
          }
          warnAndToastSimpleResult(result, t);
          return result;
        }

        const process = result.data as IRydeProcess;
        if (process?.id) {
          openDrawer<DrawerType.ProcessDrawer>({
            drawerProps: {
              type: DrawerType.ProcessDrawer,
              processId: process.id,
              processTitle: RydeProcessTitle.BulkFitVehicleAndRecalculate,
              currentEntityPage: entityFromContext,
              entity,
            },
          });
        }

        return result;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(
            t("errors.failedToRecalculateRidesPrice"),
            errorDetails,
          );
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return createErrorResultFromTranslatableItem({
            translatableItem: errorDetails,
          });
        }

        Log.errorAndToast(t("errors.failedToRecalculateRidesPrice"), error);
        setRidesErrors(normalizeErrors(error as IApiError, t));
        return createErrorResultFromException(error as TError);
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkCloneRides = useEvent(
    async (payload: IBulkCloneRidesPayload): Promise<SimpleResult<unknown>> => {
      if (companyId === undefined) {
        Log.error(t("errors.invalidCompanyId"));
        return createErrorResult(
          errors.common.invalidCompanyId,
          "Invalid company ID",
        );
      }

      try {
        setRidesErrors(undefined);
        setRidesFetching(true);
        const requestUrl = getRideBulkCloneRidesPath(companyId);
        const result = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
          body: payload,
        });

        if (result?.success) {
          const resultData = result?.data as IRydeProcess;
          openDrawer<DrawerType.ProcessDrawer>({
            drawerProps: {
              type: DrawerType.ProcessDrawer,
              processId: resultData.id,
              processTitle: RydeProcessTitle.BulkRideClone,
              entity,
            },
          });
          return result;
        }

        if (result && consumeBulkAllSkipped(result)) {
          return result;
        }

        return (
          result ||
          createErrorResult(
            errors.common.invalidAPIResponse,
            "Invalid API response",
          )
        );
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(t("errors.failedToCloneRides"), errorDetails);
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return createErrorResultFromTranslatableItem({
            translatableItem: errorDetails,
          });
        }

        Log.errorAndToast(t("errors.failedToCloneRides"), error);
        setRidesErrors(normalizeErrors(error as IApiError, t));
        return createErrorResultFromException(
          error as TError,
          "Failed to clone rides",
        );
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkRidesJourneyAssign = useEvent(
    async (payload: {
      journeyId: number;
      ids: number[];
    }): Promise<SimpleResult<unknown>> => {
      if (companyId === undefined) {
        Log.error(t("errors.invalidCompanyId"));
        return createErrorResult(
          errors.common.invalidCompanyId,
          "Invalid company ID",
        );
      }

      try {
        setRidesErrors(undefined);
        setRidesFetching(true);
        const requestUrl = getRidesJourneyAssignBulkPath(companyId);
        const result = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
          body: payload,
        });

        if (result?.success) {
          const resultData = result?.data as IRydeProcess;
          openDrawer<DrawerType.ProcessDrawer>({
            drawerProps: {
              type: DrawerType.ProcessDrawer,
              processId: resultData.id,
              processTitle: RydeProcessTitle.BulkJourneyAssign,
              currentEntityPage: entityFromContext,
              entity,
            },
          });
          return result;
        }

        if (result && consumeBulkAllSkipped(result)) {
          return result;
        }

        return (
          result ||
          createErrorResult(
            errors.common.invalidAPIResponse,
            "Invalid API response",
          )
        );
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(t("errors.failedUpdateRide"), errorDetails);
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return createErrorResultFromTranslatableItem({
            translatableItem: errorDetails,
          });
        }

        Log.errorAndToast(t("errors.failedUpdateRide"), error);
        setRidesErrors(normalizeErrors(error as IApiError, t));
        return createErrorResultFromException(
          error as TError,
          "Failed to assign journey to rides",
        );
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkSendToSupplier = useEvent(async (ids: number[]) => {
    if (companyId === undefined) {
      Log.error(t("errors.invalidCompanyId"));
      return false;
    }

    try {
      setRidesErrors(undefined);
      setRidesFetching(true);
      const requestUrl = getRideBulkSendToSupplierPath(companyId);
      const result = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
        body: { ids },
      });

      if (result?.success) {
        const resultData = result?.data as IRydeProcess;
        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkSendToSupplier,
            currentEntityPage: entityFromContext,
            entity,
          },
        });
        return true;
      }

      if (result && consumeBulkAllSkipped(result)) {
        return false;
      }

      if (result) {
        warnAndToastSimpleResult(result, t);
        setRidesErrors([simpleResultErrorInfo(result, t)]);
      }

      return false;
    } catch (error) {
      if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
        const errorDetails = toForbiddenTranslatableItem(
          error,
          errors.common.dontHavePermissionForBulkAction,
        );
        Log.errorAndToast(
          t("errors.failedToSendRidesToSupplier"),
          errorDetails,
        );
        setRidesErrors([translateInfoItem(errorDetails, t)]);
        return false;
      }

      Log.errorAndToast(t("errors.failedToSendRidesToSupplier"), error);
      setRidesErrors(normalizeErrors(error as IApiError, t));
      return false;
    } finally {
      setRidesFetching(false);
    }
  });

  const bulkSendSMS = useEvent(async (ids: number[]) => {
    if (companyId === undefined) {
      Log.error(t("errors.invalidCompanyId"));
      return false;
    }

    try {
      setRidesErrors(undefined);
      setRidesFetching(true);
      const requestUrl = getRideBulkSendSMSPath(companyId);
      const result = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
        body: { ids },
      });

      if (result?.success) {
        const resultData = result?.data as IRydeProcess;
        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkRideSendSMS,
            entity,
          },
        });
        return true;
      }

      if (result && consumeBulkAllSkipped(result)) {
        return false;
      }

      return false;
    } catch (error) {
      if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
        const errorDetails = toForbiddenTranslatableItem(
          error,
          errors.common.dontHavePermissionForBulkAction,
        );
        Log.errorAndToast(t("errors.failedToSendRidesSMS"), errorDetails);
        setRidesErrors([translateInfoItem(errorDetails, t)]);
        return false;
      }

      Log.errorAndToast(t("errors.failedToSendRidesSMS"), error);
      setRidesErrors(normalizeErrors(error as IApiError, t));
      return false;
    } finally {
      setRidesFetching(false);
    }
  });

  const bulkFetchTrackingHistory = useEvent(async (ids: number[]) => {
    if (companyId === undefined) {
      Log.error(t("errors.invalidCompanyId"));
      return false;
    }

    try {
      setRidesErrors(undefined);
      setRidesFetching(true);
      const requestUrl = getRideBulkFetchTrackingHistoryPath(companyId);
      const result = await apiPost<SimpleResult<IRydeProcess>>(requestUrl, {
        body: { ids },
      });

      if (result?.success) {
        const resultData = result?.data as IRydeProcess;
        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkFetchTrackingHistory,
            currentEntityPage: entityFromContext,
            entity,
          },
        });
        return true;
      }

      if (result && consumeBulkAllSkipped(result)) {
        return false;
      }

      if (result) {
        warnAndToastSimpleResult(result, t);
        setRidesErrors([simpleResultErrorInfo(result, t)]);
      }

      return false;
    } catch (error) {
      if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
        const errorDetails = toForbiddenTranslatableItem(
          error,
          errors.common.dontHavePermissionForBulkAction,
        );
        Log.errorAndToast(
          t("errors.failedToFetchTrackingHistory"),
          errorDetails,
        );
        setRidesErrors([translateInfoItem(errorDetails, t)]);
        return false;
      }

      Log.errorAndToast(t("errors.failedToFetchTrackingHistory"), error);
      setRidesErrors(normalizeErrors(error as IApiError, t));
      return false;
    } finally {
      setRidesFetching(false);
    }
  });

  const bulkRideRebuild = useEvent(
    async (
      body: IBulkRideRebuildPayload,
    ): Promise<SimpleResult<IRydeProcess>> => {
      try {
        setRidesErrors(undefined);
        setRidesFetching(true);

        const bulkResult = await RidesApi.bulkRideRebuild(body);

        if (!bulkResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!bulkResult.success) {
          if (consumeBulkAllSkipped(bulkResult)) {
            return castErrorResult(bulkResult);
          }
          setRidesErrors([t(errors.common.emptyApiResponse)]);
          return castErrorResult(bulkResult);
        }

        const resultData = bulkResult.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkRideRebuild,
            currentEntityPage: entityFromContext,
            entity,
          },
        });
        return bulkResult;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(t("errors.failedToRebuildRides"), errorDetails);
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        Log.errorAndToast(t("errors.failedToRebuildRides"), normalizedError);
        setRidesErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setRidesFetching(false);
      }
    },
  );

  const bulkUpdateStations = useEvent(
    async (
      body: IBulkUpdateStationsPayload,
    ): Promise<SimpleResult<IRydeProcess>> => {
      try {
        setRidesErrors(undefined);
        setRidesFetching(true);

        const bulkResult = await RidesApi.bulkUpdateStations(body);

        if (!bulkResult) {
          return simpleResultErrorEmptyResponse(t);
        }

        if (!bulkResult.success) {
          if (consumeBulkAllSkipped(bulkResult)) {
            return castErrorResult(bulkResult);
          }
          setRidesErrors([t(errors.common.emptyApiResponse)]);
          return castErrorResult(bulkResult);
        }

        const resultData = bulkResult.data as IRydeProcess;

        openDrawer<DrawerType.ProcessDrawer>({
          drawerProps: {
            type: DrawerType.ProcessDrawer,
            processId: resultData.id,
            processTitle: RydeProcessTitle.BulkRideUpdateStations,
            currentEntityPage: entityFromContext,
            entity,
          },
        });
        return bulkResult;
      } catch (error) {
        if ((error as ApiException)?.statusCode === HttpStatusCode.Forbidden) {
          const errorDetails = toForbiddenTranslatableItem(
            error,
            errors.common.dontHavePermissionForBulkAction,
          );
          Log.errorAndToast(t("errors.failedToRebuildRides"), errorDetails);
          setRidesErrors([translateInfoItem(errorDetails, t)]);
          return new SimpleResult({ error: errorDetails });
        }

        const normalizedError = normalizeErrors(error as IApiError, t);
        Log.errorAndToast(t("errors.failedToRebuildRides"), normalizedError);
        setRidesErrors(normalizedError);
        return new SimpleResult({ error: normalizedError });
      } finally {
        setRidesFetching(false);
      }
    },
  );

  return {
    bulkArchive,
    bulkSendToSupplier,
    bulkReject,
    bulkStatusNew,
    bulkStatusDone,
    bulkStatusDoneApproved,
    bulkStatusReceived,
    bulkCancel,
    bulkRebuildRoutes,
    bulkCloneRides,
    bulkRecalculatePrice,
    bulkRecalculateSupplierPrice,
    bulkFitVehicleAndRecalculate,
    bulkStarRides,
    bulkSendSMS,
    bulkFetchTrackingHistory,
    bulkRidesJourneyAssign,
    bulkRideRebuild,
    bulkUpdateStations,
  };
};
